import { STATUS_TYPE_LIBRARY } from '@process-advisor/process-miner/processMiner/statusTypeLibrary';

export function normalizeStatusName(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function tokenize(value) {
  return normalizeStatusName(value).split(' ').filter(Boolean);
}

function scoreAliasMatch(statusNorm, aliasNorm) {
  if (!statusNorm || !aliasNorm) return 0;
  if (statusNorm === aliasNorm) return 100;
  if (statusNorm.includes(aliasNorm)) return 80;
  if (aliasNorm.includes(statusNorm)) return 70;

  const statusTokens = tokenize(statusNorm);
  const aliasTokens = tokenize(aliasNorm);
  const overlap = statusTokens.filter((t) => aliasTokens.includes(t)).length;
  if (!overlap) return 0;
  return Math.round((overlap / Math.max(statusTokens.length, aliasTokens.length)) * 60);
}

function applyHeuristics(statusNorm) {
  const hits = [];
  const add = (operationalType, timeBucket, confidence, reason) => {
    hits.push({ operationalType, timeBucket, confidence, reason });
  };

  if (/\b(pending|awaiting|waiting|blocked|hold|on hold|deferred|parked|paused|stalled|suspended)\b/.test(statusNorm)) {
    add('waiting', 'wait', 0.86, 'Waiting/hold keyword');
  }
  if (/\b(assigned|accepted|dispatch|dispatched|in progress|progress|working|investigating|analysis|analyzing|analysing|implementing|development|developing|fixing|processing|wip)\b/.test(statusNorm)) {
    add('active', 'touch', 0.9, 'Active work keyword');
  }
  if (/\b(review|approval|approve|qa|test|testing|uat|validation|verify|verification)\b/.test(statusNorm)) {
    add('review', 'touch', 0.78, 'Review/approval keyword');
  }
  if (/\b(open|new|backlog|triage|todo|to do|ready|queued|incoming|submitted|created|registered|logged)\b/.test(statusNorm)) {
    add('intake', 'queue', 0.84, 'Intake keyword');
  }
  if (/\b(resolved|done|completed|complete|fixed|fulfilled|restored|delivered)\b/.test(statusNorm)) {
    add('resolved', 'admin', 0.88, 'Resolved/completed keyword');
  }
  if (/\b(closed|finalized|finalised|archived|finished)\b/.test(statusNorm)) {
    add('closed', 'terminal', 0.95, 'Closed/final keyword');
  }
  if (/\b(cancelled|canceled|rejected|declined|duplicate|abandoned|withdrawn|invalid|wont do|won't do)\b/.test(statusNorm)) {
    add('cancelled', 'terminal', 0.95, 'Cancelled/rejected keyword');
  }
  return hits;
}

export function getStatusTypeFlags(operationalType, timeBucket) {
  return {
    isStartState: operationalType === 'intake',
    isActiveWork: timeBucket === 'touch',
    isWaiting: timeBucket === 'wait',
    isTerminal: timeBucket === 'terminal',
    isSynthetic: timeBucket === 'no_time' || operationalType === 'synthetic',
    excludeFromFlowEfficiency: ['queue', 'admin', 'terminal', 'no_time', 'unclassified'].includes(timeBucket)
  };
}

export function suggestStatusMapping(statusName) {
  const statusNorm = normalizeStatusName(statusName);
  let best = null;

  for (const entry of STATUS_TYPE_LIBRARY) {
    const aliasScores = entry.aliases.map((alias) => {
      const aliasNorm = normalizeStatusName(alias);
      return { alias, score: scoreAliasMatch(statusNorm, aliasNorm) };
    });
    const topAlias = aliasScores.sort((a, b) => b.score - a.score)[0];
    if (!topAlias || topAlias.score <= 0) continue;

    const weightedScore = Math.round(topAlias.score * entry.confidence);
    if (!best || weightedScore > best.weightedScore) {
      best = {
        statusName,
        normalizedStatus: statusNorm,
        operationalType: entry.operationalType,
        timeBucket: entry.timeBucket,
        confidence: Math.min(0.99, weightedScore / 100),
        flags: { ...entry.flags },
        matchSource: 'library',
        matchedAlias: topAlias.alias,
        weightedScore,
        reason: `Library match: ${topAlias.alias}`
      };
    }
  }

  const heuristicHits = applyHeuristics(statusNorm);
  if (heuristicHits.length) {
    const topHeuristic = heuristicHits.sort((a, b) => b.confidence - a.confidence)[0];
    const heuristicScore = Math.round(topHeuristic.confidence * 100);
    if (!best || heuristicScore > best.weightedScore) {
      best = {
        statusName,
        normalizedStatus: statusNorm,
        operationalType: topHeuristic.operationalType,
        timeBucket: topHeuristic.timeBucket,
        confidence: topHeuristic.confidence,
        flags: getStatusTypeFlags(topHeuristic.operationalType, topHeuristic.timeBucket),
        matchSource: 'heuristic',
        matchedAlias: null,
        weightedScore: heuristicScore,
        reason: `Heuristic: ${topHeuristic.reason}`
      };
    }
  }

  if (!best) {
    best = {
      statusName,
      normalizedStatus: statusNorm,
      operationalType: 'other',
      timeBucket: 'queue',
      confidence: 0.35,
      flags: getStatusTypeFlags('other', 'queue'),
      matchSource: 'fallback',
      matchedAlias: null,
      weightedScore: 35,
      reason: 'No strong library or heuristic match'
    };
  }

  return best;
}

export function autoMapStatuses(statusNames = []) {
  const unique = [...new Set((statusNames || []).map((s) => String(s || '').trim()).filter(Boolean))];
  return unique.map((statusName) => suggestStatusMapping(statusName));
}

function uniqueStatusNames(statusNames = []) {
  return [...new Set((statusNames || []).map((s) => String(s || '').trim()).filter(Boolean))];
}

function normalizeSourceSystem(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'snow' || raw === 'servicenow' || raw === 'service_now') return 'servicenow';
  if (raw === 'forge' || raw === 'jira' || raw === 'atlassian') return 'jira';
  return raw || 'generic';
}

function normalizeBucket(value) {
  const raw = normalizeStatusName(value);
  if (['touch', 'active', 'work', 'working'].includes(raw)) return 'touch';
  if (['wait', 'waiting', 'hold', 'on hold', 'blocked'].includes(raw)) return 'wait';
  if (['admin', 'resolved', 'resolution', 'closure', 'closure delay'].includes(raw)) return 'admin';
  if (['queue', 'intake', 'backlog'].includes(raw)) return 'queue';
  if (['terminal', 'closed', 'done', 'cancelled', 'canceled'].includes(raw)) return 'terminal';
  if (['synthetic', 'start end', 'ignore', 'no time', 'none'].includes(raw)) return 'no_time';
  if (['unclassified', 'unknown'].includes(raw)) return 'unclassified';
  return '';
}

function normalizeOperationalType(value, fallbackBucket = '') {
  const raw = normalizeStatusName(value);
  if (['intake', 'queue'].includes(raw)) return 'intake';
  if (['active', 'touch', 'work', 'working'].includes(raw)) return 'active';
  if (['waiting', 'wait', 'hold', 'blocked'].includes(raw)) return 'waiting';
  if (['review', 'approval', 'qa'].includes(raw)) return 'review';
  if (['resolved', 'resolution', 'admin'].includes(raw)) return 'resolved';
  if (['closed', 'terminal'].includes(raw)) return 'closed';
  if (['cancelled', 'canceled', 'rejected', 'invalid'].includes(raw)) return 'cancelled';
  if (['synthetic', 'start', 'end', 'ignore', 'no time'].includes(raw)) return 'synthetic';
  if (['unclassified', 'other', 'unknown'].includes(raw)) return 'unclassified';

  switch (fallbackBucket) {
    case 'touch': return 'active';
    case 'wait': return 'waiting';
    case 'admin': return 'resolved';
    case 'queue': return 'intake';
    case 'terminal': return 'closed';
    case 'no_time': return 'synthetic';
    case 'unclassified': return 'unclassified';
    default: return raw || 'other';
  }
}

function makeMapping({ statusName, operationalType, timeBucket, confidence = 1, matchSource, reason, matchedAlias = null }) {
  return {
    statusName,
    normalizedStatus: normalizeStatusName(statusName),
    operationalType,
    timeBucket,
    confidence,
    flags: getStatusTypeFlags(operationalType, timeBucket),
    matchSource,
    matchedAlias,
    weightedScore: Math.round(confidence * 100),
    reason
  };
}

const SERVICENOW_LIFECYCLE_DEFAULTS = [
  // synthetic nodes generated by the process miner
  { aliases: ['start'], operationalType: 'synthetic', timeBucket: 'no_time', reason: 'Synthetic Start node; excluded from flow-time buckets' },
  { aliases: ['end'], operationalType: 'synthetic', timeBucket: 'no_time', reason: 'Synthetic End node; excluded from flow-time buckets' },

  // intake / backlog / not yet picked up
  { aliases: ['new', 'open', 'opened', 'unassigned', 'awaiting assignment', 'pending assignment', 'queued', 'queue', 'backlog'], operationalType: 'intake', timeBucket: 'queue', reason: 'ServiceNow intake/queue state' },

  // active work
  { aliases: ['assigned', 'accepted', 'dispatch', 'dispatched', 'work in progress', 'in progress', 'active', 'investigating', 'analysis', 'implement', 'implementing', 'scheduled', 'assess', 'authorize'], operationalType: 'active', timeBucket: 'touch', reason: 'ServiceNow active work state' },

  // waiting / dependency states
  { aliases: ['pending', 'on hold', 'hold', 'awaiting', 'waiting', 'waiting for user', 'waiting for customer', 'waiting for caller', 'waiting for vendor', 'waiting for approval', 'awaiting approval', 'blocked', 'deferred'], operationalType: 'waiting', timeBucket: 'wait', reason: 'ServiceNow waiting/dependency state' },

  // post-resolution / closure admin
  { aliases: ['resolved', 'complete', 'completed', 'done', 'fulfilled', 'restored', 'review', 'close review'], operationalType: 'resolved', timeBucket: 'admin', reason: 'ServiceNow resolved/closure-admin state' },

  // terminal states
  { aliases: ['closed', 'cancelled', 'canceled', 'invalid', 'rejected', 'duplicate', 'withdrawn'], operationalType: 'closed', timeBucket: 'terminal', reason: 'ServiceNow terminal state' }
];

function buildDefaultLookup(rows) {
  const lookup = new Map();
  rows.forEach((row) => {
    row.aliases.forEach((alias) => {
      lookup.set(normalizeStatusName(alias), row);
    });
  });
  return lookup;
}

const SERVICENOW_LIFECYCLE_DEFAULT_LOOKUP = buildDefaultLookup(SERVICENOW_LIFECYCLE_DEFAULTS);

export function isServiceNowMetricLifecycleField(activityContext = {}) {
  const kind = String(activityContext?.activity_kind || activityContext?.analysis_type || '').trim().toLowerCase();
  const field = String(activityContext?.activity_field || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  if (['lifecycle_status', 'change_lifecycle', 'change_phase'].includes(kind)) return true;
  if (['state', 'status', 'stage', 'workflow_state', 'workflow_status', 'incident_state', 'change_state'].includes(field)) return true;
  return false;
}

function serviceNowDefaultMapping(statusName) {
  const statusNorm = normalizeStatusName(statusName);
  const direct = SERVICENOW_LIFECYCLE_DEFAULT_LOOKUP.get(statusNorm);
  if (direct) {
    return makeMapping({
      statusName,
      operationalType: direct.operationalType,
      timeBucket: direct.timeBucket,
      confidence: 1,
      matchSource: 'servicenow_default',
      matchedAlias: statusNorm,
      reason: direct.reason
    });
  }

  let best = null;
  for (const row of SERVICENOW_LIFECYCLE_DEFAULTS) {
    for (const alias of row.aliases) {
      const aliasNorm = normalizeStatusName(alias);
      const score = scoreAliasMatch(statusNorm, aliasNorm);
      if (score <= 0) continue;
      if (!best || score > best.score) best = { row, alias, score };
    }
  }

  if (best && best.score >= 80) {
    return makeMapping({
      statusName,
      operationalType: best.row.operationalType,
      timeBucket: best.row.timeBucket,
      confidence: Math.min(0.98, best.score / 100),
      matchSource: 'servicenow_default_alias',
      matchedAlias: best.alias,
      reason: best.row.reason
    });
  }

  const mapped = suggestStatusMapping(statusName);
  if (mapped.matchSource !== 'fallback') {
    return {
      ...mapped,
      matchSource: `servicenow_lifecycle_${mapped.matchSource}`,
      reason: `ServiceNow lifecycle/status fallback mapping: ${mapped.reason}`
    };
  }

  return makeMapping({
    statusName,
    operationalType: 'unclassified',
    timeBucket: 'unclassified',
    confidence: 1,
    matchSource: 'servicenow_lifecycle_unclassified',
    reason: 'ServiceNow lifecycle/status value has no default or explicit metric mapping'
  });
}

function serviceNowNeutralMapping(statusName, reason = 'ServiceNow activity field is not a lifecycle/status field') {
  return makeMapping({
    statusName,
    operationalType: 'unclassified',
    timeBucket: 'unclassified',
    confidence: 1,
    matchSource: 'servicenow_unclassified',
    reason
  });
}

function parseActivityGroupMappingConfig(config) {
  if (!config) return null;
  if (typeof config === 'string') {
    try { return JSON.parse(config); } catch { return null; }
  }
  if (typeof config === 'object') return config;
  return null;
}

function rowsFromExplicitConfig(config) {
  if (!config) return [];
  if (Array.isArray(config)) return config;

  const candidates = [
    config.metric_mappings,
    config.metricMappings,
    config.status_metric_mappings,
    config.statusMetricMappings,
    config.activity_metric_mappings,
    config.activityMetricMappings,
    config.mappings,
    config.rows,
    config.items
  ];

  for (const rows of candidates) {
    if (Array.isArray(rows)) return rows;
  }

  return [];
}

function buildExplicitServiceNowMappingLookup(activityGroupMappings) {
  const config = parseActivityGroupMappingConfig(activityGroupMappings);
  const rows = rowsFromExplicitConfig(config);
  const lookup = new Map();

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;

    const keys = [
      row.value,
      row.raw_value,
      row.rawValue,
      row.activity,
      row.status,
      row.statusName,
      row.label,
      row.name,
      row.group,
      row.mapped_value,
      row.mappedValue
    ].map((v) => normalizeStatusName(v)).filter(Boolean);

    if (!keys.length) return;

    const timeBucket = normalizeBucket(
      row.timeBucket || row.time_bucket || row.metricBucket || row.metric_bucket || row.bucket || row.durationBucket || row.duration_bucket
    );
    const operationalType = normalizeOperationalType(
      row.operationalType || row.operational_type || row.type || row.metricType || row.metric_type,
      timeBucket
    );

    if (!timeBucket && !operationalType) return;

    const mapping = {
      operationalType: operationalType || normalizeOperationalType('', timeBucket),
      timeBucket: timeBucket || normalizeBucket(operationalType) || 'unclassified',
      reason: 'Explicit ServiceNow metric mapping'
    };

    keys.forEach((key) => lookup.set(key, mapping));
  });

  return lookup;
}

function serviceNowExplicitMapping(statusName, explicitLookup) {
  const explicit = explicitLookup?.get(normalizeStatusName(statusName));
  if (!explicit) return null;
  const operationalType = explicit.operationalType || 'unclassified';
  const timeBucket = explicit.timeBucket || 'unclassified';
  return makeMapping({
    statusName,
    operationalType,
    timeBucket,
    confidence: 1,
    matchSource: 'servicenow_explicit',
    reason: explicit.reason || 'Explicit ServiceNow metric mapping'
  });
}

export function mapActivityValueForMetrics(statusName, options = {}) {
  const sourceSystem = normalizeSourceSystem(options.sourceSystem || options.platform || options.appVersion);

  if (sourceSystem !== 'servicenow') {
    return suggestStatusMapping(statusName);
  }

  const explicitLookup = options.explicitLookup || buildExplicitServiceNowMappingLookup(options.activityGroupMappings);
  const explicit = serviceNowExplicitMapping(statusName, explicitLookup);
  if (explicit) return explicit;

  if (isServiceNowMetricLifecycleField(options.activityContext)) {
    return serviceNowDefaultMapping(statusName);
  }

  const kind = String(options.activityContext?.activity_kind || options.activityContext?.analysis_type || 'activity').trim();
  return serviceNowNeutralMapping(
    statusName,
    `ServiceNow ${kind} field has no explicit metric mapping; no status heuristic applied`
  );
}

export function mapStatusesForSource(statusNames = [], options = {}) {
  const unique = uniqueStatusNames(statusNames);
  const sourceSystem = normalizeSourceSystem(options.sourceSystem || options.platform || options.appVersion);

  if (sourceSystem !== 'servicenow') {
    return unique.map((statusName) => suggestStatusMapping(statusName));
  }

  const explicitLookup = buildExplicitServiceNowMappingLookup(options.activityGroupMappings);
  return unique.map((statusName) => mapActivityValueForMetrics(statusName, { ...options, sourceSystem, explicitLookup }));
}

export function buildStatusTypeSummary(statusMappings = []) {
  const groupCount = (rows, key) => rows.reduce((acc, row) => {
    const value = row[key] || 'unknown';
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

  return {
    total_statuses: statusMappings.length,
    by_operational_type: groupCount(statusMappings, 'operationalType'),
    by_time_bucket: groupCount(statusMappings, 'timeBucket'),
    statuses: statusMappings.map((s) => ({
      status: s.statusName,
      operational_type: s.operationalType,
      time_bucket: s.timeBucket,
      confidence: s.confidence,
      match_source: s.matchSource,
      reason: s.reason
    }))
  };
}
